import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';

import { RootState } from '../../models/RootState';
import './FancyTable.scss';
import { EventData, FancyMarkets } from '../../models/EventData';
import { Accordion, AccordionSummary, Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import MarketOddBtn from '../MarketOddBtn/marketOddBtn';
import ExpandLessSharp from '@mui/icons-material/ExpandLessSharp';
import moment from 'moment';
import { getFancyMarkets } from '../../store/exchangeSports/exchangeSportsAction';
import ExchBetSlip from '../ExchBetSlip/ExchBetSlip';
import { BsData, UserBet } from '../../models/ExchBetSlip';
import { addExchangeBet } from '../../store/exchBetSlip/exchBetSlipAction';
import Modal from '../Modal/Modal';
import FancyBookView from './FancyBookView/FancyBookView';



type StoreProps = {
  bets: BsData[];
  addExchangeBet: (data: BsData) => void;
  fancyMarkets: FancyMarkets[];
  eventData: EventData;
  openBets: UserBet[];
  isOneClickBettingEnabled: boolean;
};

type OddsInfoMsg = {
  launch: boolean;
  oddsType: string;
  eventTypeID: string;
};

const FMTable: React.FC<StoreProps> = (props) => {
  const {
    fancyMarkets,
    eventData,
    bets,
    addExchangeBet,
    openBets,
    isOneClickBettingEnabled
  } = props;

  const disabledStatus = ['suspended', 'suspended-manually', 'ball_running'];
  const [selectedRow, setSelectedRow] = useState<string>('');
  const [fancyCategories, setFancyCategories] = useState<Set<string>>(new Set());
  const [showBooksModal, setShowBooksModal] = useState<boolean>(false);
  const [fancyBookOutcomeId, setFancyBookOutcomeId] = useState<string>("");
  const [tabVal, setTabVal] = useState(0);
  const [filteredFancyMarketsData, setFilteredFancyMarketsData] = useState<FancyMarkets[]>([]);
  const fancyCategoriesOrder = [
    { fancyCategory: 'All', value: 'ALL' },
    { fancyCategory: 'sessions', value: 'SESSIONS' },
    { fancyCategory: 'wpmarket', value: 'W/P MARKET' },
    { fancyCategory: 'extramarket', value: 'EXTRA MARKET' },
    { fancyCategory: 'oddeven', value: 'ODD/EVEN' },
  ];
  const [exposureMap, setExposureMap] = useState<any>({});

  useEffect(() => {
    let localFancyCategories = new Set<string>();
    for (const fm of fancyMarkets) {
      localFancyCategories.add(fm.category);
    }
    fancyMarkets.map((fm) => localFancyCategories.add(fm.category));
    setFancyCategories(localFancyCategories);
  }, [fancyMarkets]);

  const handletabs = useCallback(async (localFancyCategory: string) => {
    setFilteredFancyMarketsData(
      localFancyCategory === 'All'
        ? fancyMarkets
        : fancyMarkets.filter((fm) => {
          return fm.category === localFancyCategory;
        })
    );
  }, []);

  useEffect(() => {
    handletabs('All');
  }, []);

  useEffect(() => {
    calculateExposure(openBets)
  }, [openBets]);

  const calculateExposure = (bets: UserBet[]) => {
    let newExposureMap: any = {};

    // Step 1: Group runners per market
    let marketRunnersMap: { [key: string]: Set<number> } = {};

    bets.forEach((bet) => {
      if (!marketRunnersMap[bet.marketId]) {
        marketRunnersMap[bet.marketId] = new Set();
      }
      marketRunnersMap[bet.marketId].add(bet.sessionOutcome);
      marketRunnersMap[bet.marketId].add(bet.sessionOutcome - 1);
    });

    bets.forEach((bet) => {
      if (bet.marketType === "FANCY") {
        const marketId = bet.marketId;

        if (!newExposureMap[marketId]) {
          newExposureMap[marketId] = {};
        }

        const uniqueRunners = Array.from(marketRunnersMap[marketId]);

        uniqueRunners.forEach((runnerId: any) => {
          if (!newExposureMap[marketId][runnerId]) {
            newExposureMap[marketId][runnerId] = {
              runnerId: runnerId.toString(),
              userRisk: 0,
            };
          }
        });

        // Apply correct risk calculation based on bet type
        uniqueRunners.forEach((runnerId: any) => {
          if (bet.betType === "BACK") {
            if (runnerId < bet.sessionOutcome) {
              newExposureMap[marketId][runnerId].userRisk -= bet.stakeAmount;
            } else if (runnerId >= bet.sessionOutcome) {
              newExposureMap[marketId][runnerId].userRisk += (bet?.oddValue * 0.01) * bet.stakeAmount;
            }
          } else if (bet.betType === "LAY") {
            if (runnerId < bet.sessionOutcome) {
              newExposureMap[marketId][runnerId].userRisk += bet.stakeAmount;
            } else if (runnerId >= bet.sessionOutcome) {
              newExposureMap[marketId][runnerId].userRisk -= (bet?.oddValue * 0.01) * bet.stakeAmount;
            }
          }
        });
      }
    });
    setExposureMap(newExposureMap);
  };

  const getFancyMarketsByGroup = (category: string) => {
    return fancyMarkets
      .filter((fm) => fm.category === category)
      .sort((a, b) => {
        const aSort = a?.sort ? Number(a.sort) : 0;
        const bSort = b?.sort ? Number(b.sort) : 0;
        if (aSort !== bSort) {
          return aSort - bSort;
        }
        const aDesc = a.marketName;
        const bDesc = b.marketName;
        return aDesc.localeCompare(bDesc);
      });
  };

  return (
    <>
      <div className="fm-table-ctn">
        <div className="fm-table-content table-ctn">
          <TableContainer component={Paper}>
            <Table className="fm-table">
              <TableHead>
                <TableRow>
                  <TableCell className="tabs-table-cell" colSpan={12}>
                    <div className="tabs-fancy">
                      {fancyCategories.size > 0 ? (
                        <span
                          className={tabVal === 0 ? 'sel-tab' : 'ind-tab'}
                          onClick={() => {
                            handletabs('All');
                            setTabVal(0);
                          }}
                        >
                          <div>{'All'}</div>
                        </span>
                      ) : null}
                      {fancyCategoriesOrder.map((fc, index) => {
                        return fancyCategories.has(fc.fancyCategory) ? (
                          <span
                            className={
                              tabVal === index + 1 ? 'sel-tab' : 'ind-tab'
                            }
                            onClick={() => {
                              setTabVal(index + 1);
                              handletabs(fc.fancyCategory);
                            }}
                          >
                            <div>{fc.value}</div>
                          </span>
                        ) : null;
                      })}
                    </div>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredFancyMarketsData &&
                  filteredFancyMarketsData.length > 0 ? (
                  <>
                    {fancyCategoriesOrder.map((group) => (
                      <>
                        {filteredFancyMarketsData.filter(
                          (fm) =>
                            fm.category === group.fancyCategory &&
                            fancyCategories.has(group.fancyCategory)
                        ).length > 0 ? (
                          <>
                            <Accordion
                              defaultExpanded={true}
                              className="markets-accordian"
                            >
                              <AccordionSummary
                                expandIcon={
                                  <ExpandLessSharp className="expand-icon" />
                                }
                                aria-controls="panel1a-content"
                              >
                                <FancyHeaderRow
                                  groupName={group.value}
                                />
                              </AccordionSummary>
                              <FancyHeaderRow
                                groupName={group.value}
                                className="row-hidden"
                              />
                              {getFancyMarketsByGroup(group.fancyCategory).map(
                                (fmMarket, idx) => {
                                  return (
                                    <>
                                      <FancyMarketRow
                                        isOneClickBettingEnabled={isOneClickBettingEnabled}
                                        index={idx}
                                        fancyMarket={fmMarket}
                                        disabledStatus={disabledStatus}
                                        eventData={eventData}
                                        addExchangeBet={addExchangeBet}
                                        bets={bets}
                                        selectedRow={selectedRow}
                                        setShowBooksModal={() => {
                                          setFancyBookOutcomeId(
                                            fmMarket?.marketId
                                          );
                                          setShowBooksModal(true);
                                        }}
                                        exposureMap={exposureMap}
                                        setSelectedRow={setSelectedRow}
                                        min={fmMarket?.limits?.minBetValue}
                                        max={fmMarket?.limits?.maxBetValue}
                                      />
                                    </>
                                  );
                                }
                              )}
                            </Accordion>
                          </>
                        ) : null}
                      </>
                    ))}
                  </>
                ) : (
                  <TableRow>
                    <TableCell colSpan={3} style={{ background: 'transparent' }}>
                      <div className="no-fancy-msg">
                        {'No Fancy Markets'}
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Modal
            open={showBooksModal}
            closeHandler={() => {
              setShowBooksModal(false);
              setFancyBookOutcomeId("");
            }}
            customClass="fancy-book-dialog"
            title="Book List"
            size="sm"
          >
            <FancyBookView
              exposureMap={
                exposureMap &&
                  exposureMap[fancyBookOutcomeId]
                  ? exposureMap[fancyBookOutcomeId]
                  : {}
              }
            />
          </Modal>
        </div>
      </div>
    </>
  );
};

type FancyHeaderRowProps = {
  groupName: string;
  className?: string;
};

const FancyHeaderRow: React.FC<FancyHeaderRowProps> = (props) => {
  const { groupName, className } = props;
  const tableFields = [
    {
      key: 'groupName',
      Label: groupName,
      className: 'market-name-cell-head',
      align: 'left',
    },
    {
      key: 'odds-no',
      Label: '',
      className: 'odds-cell-head book-btn-cell',
      align: 'center',
    },
    {
      key: 'odds-no',
      Label: 'No',
      className: 'odds-cell-head odds-no-cell',
      align: 'center',
    },
    {
      key: 'odds-yes',
      Label: 'Yes',
      className: 'odds-cell-head odds-yes-cell',
      align: 'center',
    },
    {
      key: 'limits',
      Label: '',
      className: 'odds-cell-head limits-cell',
      align: 'center',
    },
  ];

  return (
    <TableRow className={'header-row ' + className}>
      {tableFields.map((tF, index) => (
        <TableCell
          key={tF.key + index}
          align={tF.align === 'left' ? 'left' : 'center'}
          className={tF.className}
        >
          {tF.key === 'odds-no' ||
            tF.key === 'odds-yes' ||
            tF.key === 'groupName' ? (
            <div className={tF.key.toLowerCase() + '-cell'}>
              {tF.Label?.toUpperCase()}
            </div>
          ) : null}
        </TableCell>
      ))}
    </TableRow>
  );
};

type FancyMarketRowProps = {
  fancyMarket: FancyMarkets;
  index: number;
  eventData: EventData;
  disabledStatus: string[];
  selectedRow: string;
  setSelectedRow: (data: any) => void;
  addExchangeBet: (data: BsData) => void;
  bets: BsData[];
  exposureMap: any;
  setShowBooksModal: () => void;
  // fetchBetLimits: (mId, mcategory) => void;
  max: number;
  min: number;
  isOneClickBettingEnabled: boolean;
};

const FancyMarketRow: React.FC<FancyMarketRowProps> = (props) => {
  const {
    fancyMarket,
    index,
    disabledStatus,
    eventData,
    addExchangeBet,
    setSelectedRow,
    setShowBooksModal,
    bets,
    // fetchBetLimits,
    exposureMap,
    min,
    max,
    isOneClickBettingEnabled
  } = props;

  return (
    <>
      <TableRow key={'row-' + index}>
        <TableCell className="market-name-cell" key={'row-' + index + 'cell-1'}>
          <div className="market">
            {fancyMarket.marketName}{' '}
          </div>
        </TableCell>
        <TableCell
          className="odds-cell book-btn-cell"
          key={'row-' + index + 'cell-4'}
        >
          <Button
            className="fancy-book-btn"
            onClick={() => {
              if (
                exposureMap &&
                exposureMap[`${fancyMarket.marketId}`]
              )
                setShowBooksModal();
            }}
            disabled={
              !(
                exposureMap &&
                exposureMap[`${fancyMarket.marketId}`]
              )
            }
          >
            {'Book'}
          </Button>
        </TableCell>
        <TableCell className="odds-cell" key={'row-' + index + 'cell-2'}>
          <div className="odds-block">
            <MarketOddBtn
              mainValue={
                disabledStatus.includes(fancyMarket.status.toLowerCase()) ?
                  0 : fancyMarket.noValue ? Number(fancyMarket.noValue).toFixed() : 0
              }
              mainValueClass="runs"
              subValue={
                disabledStatus.includes(fancyMarket.status.toLowerCase()) ?
                  0 : fancyMarket.noRate ? fancyMarket.noRate : 0}
              subValueClass="odds"
              oddType="odds-no-cell"
              valueType="fancyMarketOdds"
              disable={disabledStatus.includes(fancyMarket.status.toLowerCase()) ||
                moment(eventData.openDate).diff(moment(), 'minutes') > 60 * 24 * 2}
              onClick={() => {
                if (
                  !disabledStatus.includes(fancyMarket.status.toLowerCase())
                ) {
                  setSelectedRow(fancyMarket.marketName + 'FM');
                  addExchangeBet({
                    eventId: eventData?.eventId,
                    eventName: eventData?.eventName,
                    marketId: fancyMarket?.marketId,
                    marketType: "FANCY",
                    marketName: fancyMarket?.marketName,
                    oddValue: fancyMarket?.noRate,
                    sessionOutcome: fancyMarket?.noValue,
                    betType: "LAY",
                    stakeAmount: 0,
                    runnerId: fancyMarket?.marketId,
                    runnerName: fancyMarket?.marketName,
                    competitionId: eventData?.competitionId,
                    sportId: eventData?.sportId,
                    maxLimit: max,
                    minLimit: min,
                    provider: eventData?.markets?.fancyProvider || ""
                  });
                }
              }}
            />
          </div>
        </TableCell>

        <TableCell className="odds-cell" key={'row-' + index + 'cell-3'}>
          <div className="odds-block">
            <MarketOddBtn
              mainValue={
                disabledStatus.includes(fancyMarket.status.toLowerCase()) ?
                  0 : Number(fancyMarket.yesValue).toFixed()}
              mainValueClass="runs"
              subValue={fancyMarket.yesRate}
              subValueClass="odds"
              oddType="odds-yes-cell"
              valueType="fancyMarketOdds"
              disable={disabledStatus.includes(fancyMarket.status.toLowerCase()) ||
                moment(eventData.openDate).diff(moment(), 'minutes') > 60 * 24 * 2}
              onClick={() => {
                if (
                  !disabledStatus.includes(fancyMarket.status.toLowerCase())
                ) {
                  setSelectedRow(fancyMarket.marketName + 'FM');
                  addExchangeBet({
                    eventId: eventData?.eventId,
                    eventName: eventData?.eventName,
                    marketId: fancyMarket?.marketId,
                    marketType: "FANCY",
                    marketName: fancyMarket?.marketName,
                    oddValue: fancyMarket.yesRate,
                    sessionOutcome: fancyMarket.yesValue,
                    betType: "BACK",
                    stakeAmount: 0,
                    runnerId: fancyMarket?.marketId,
                    runnerName: fancyMarket?.marketName,
                    competitionId: eventData?.competitionId,
                    sportId: eventData?.sportId,
                    maxLimit: max,
                    minLimit: min,
                    provider: eventData?.markets?.fancyProvider || ""
                  });
                }
              }}
            />
          </div>
        </TableCell>

        <TableCell className="limits-cell">
          <div className="limits-data">
            <div className="row mob-view">
              <div>
                MIN:{' '}
                {fancyMarket?.limits?.minBetValue}
              </div>{' '}
              <div>
                {' '}
                MAX:{' '}
                {fancyMarket?.limits?.maxBetValue % 1000 === 0 ? fancyMarket?.limits?.maxBetValue / 1000 + "K" : fancyMarket?.limits?.maxBetValue}
              </div>
            </div>
          </div>
        </TableCell>
        {disabledStatus.includes(fancyMarket.status.toLowerCase()) ? (
          <div className="disabled-odds-cell" key={'row-' + index + 'cell-5'}>
            {fancyMarket.status.toLowerCase().includes('suspended')
              ? 'SUSPENDED'
              : fancyMarket.status.replace('_', ' ')}
          </div>
        ) : null}
      </TableRow>
      {!isOneClickBettingEnabled && bets?.length > 0 &&
        bets?.[0]?.marketName === fancyMarket?.marketName &&
        bets?.[0]?.marketId === fancyMarket?.marketId &&
        // isMobile ? (
        <TableRow className="inline-betslip">
          <TableCell colSpan={12}>
            {' '}
            <ExchBetSlip balance={0} exposure={0} />{' '}
          </TableCell>
        </TableRow>
        // ) : null
      }
    </>
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    bets: state.exchBetSlip.bets,
    eventData: state.exchangeSports.eventInfo,
    fancyMarkets: getFancyMarkets(state.exchangeSports.eventInfo),
    openBets: state?.exchBetSlip?.openBets,
    isOneClickBettingEnabled: state.exchBetSlip.isOneClickBettingEnabled,
  };
};

const mapDispatchToProps = (dispatch: Function) => {
  return {
    addExchangeBet: (data: BsData) => dispatch(addExchangeBet(data)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(FMTable);
